import { useCallback, useEffect, useRef } from "react";

/**
 * Tracks which "page-<n>" marker div is currently in view and reflects that
 * page number in the URL (client-side only, no navigation/refetch). Callers
 * register marker refs via `setPageRef(page)` so both statically rendered
 * sections and infinite-scroll-loaded sections can share a single observer.
 */
export function usePageNumberSync() {
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const currentUrlPageRef = useRef<number>(1);
  const lastScrollYRef = useRef<number>(0);
  const scrollDirectionRef = useRef<"up" | "down">("down");

  const updateUrlPage = useCallback((page: number) => {
    const url = new URL(window.location.href);
    if (page <= 1) {
      url.searchParams.delete("page");
      window.history.replaceState(window.history.state, "", url);
      currentUrlPageRef.current = page;
      return;
    }
    if (currentUrlPageRef.current === page) return;
    currentUrlPageRef.current = page;

    url.searchParams.set("page", page.toString());
    window.history.replaceState(window.history.state, "", url);
  }, []);

  useEffect(() => {
    lastScrollYRef.current = window.scrollY;

    const handleScroll = () => {
      const currentY = window.scrollY;
      scrollDirectionRef.current =
        currentY > lastScrollYRef.current ? "down" : "up";
      lastScrollYRef.current = currentY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);
        if (visibleEntries.length > 0) {
          // on scroll down: pick the topmost entry; on scroll up: pick the bottommost
          const selectedEntry =
            scrollDirectionRef.current === "down"
              ? visibleEntries.reduce((prev, curr) =>
                  prev.boundingClientRect.top < curr.boundingClientRect.top
                    ? prev
                    : curr,
                )
              : visibleEntries.reduce((prev, curr) =>
                  prev.boundingClientRect.top > curr.boundingClientRect.top
                    ? prev
                    : curr,
                );

          const id = selectedEntry.target.id; // e.g. "page-2"
          const page = Number(id.replace("page-", ""));
          if (!Number.isNaN(page)) {
            updateUrlPage(page);
          }
        }
      },
      {
        // fires when a section crosses the vertical middle of the viewport
        rootMargin: "-45% 0px -45% 0px",
        threshold: 0,
      },
    );

    pageRefs.current.forEach((node) => observerRef.current?.observe(node));

    return () => observerRef.current?.disconnect();
  }, [updateUrlPage]);

  const setPageRef = useCallback(
    (page: number) => (node: HTMLDivElement | null) => {
      const existing = pageRefs.current.get(page);
      if (existing && existing !== node) {
        observerRef.current?.unobserve(existing);
      }
      if (node) {
        pageRefs.current.set(page, node);
        observerRef.current?.observe(node);
      } else {
        pageRefs.current.delete(page);
      }
    },
    [],
  );

  return { setPageRef };
}
